import { OnHoldPeriod, Ticket } from '../data/mockData'

export type UserTicketMetrics = {
  activeTickets: number
  avgResponseTime: number
  avgResolutionTime: number
}

export type TicketMetrics = {
  activeTickets: number
  receivedToday: number
  avgFirstResponse: number
  avgResolutionTime: number
  waitingForResponse: number
  closedToday: number
  avgActiveAge: number
}

export function getOnHoldMinutes(
  periods: OnHoldPeriod[] | undefined,
  referenceTime = new Date(),
): number {
  if (!periods?.length) return 0

  const referenceTimestamp = referenceTime.getTime()
  return periods.reduce((total, period) => {
    const startedAt = new Date(period.startedAt).getTime()
    const endedAt = period.endedAt
      ? new Date(period.endedAt).getTime()
      : referenceTimestamp

    if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt <= startedAt) {
      return total
    }

    return total + (endedAt - startedAt) / 60000
  }, 0)
}

export function getActiveDurationMinutes(
  ticket: Ticket,
  referenceTime = new Date(),
): number {
  const startedAt = new Date(ticket.createdDate).getTime()
  const endedAt = ticket.closedDate
    ? new Date(ticket.closedDate).getTime()
    : referenceTime.getTime()

  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt <= startedAt) {
    return 0
  }

  return Math.max(
    0,
    (endedAt - startedAt) / 60000 - getOnHoldMinutes(ticket.onHoldPeriods, referenceTime),
  )
}

export function getAdjustedResolutionHours(ticket: Ticket): number {
  return Math.max(0, ticket.resolutionTime - getOnHoldMinutes(ticket.onHoldPeriods) / 60)
}

export function getUserTicketMetrics(
  tickets: Ticket[],
  assignee: string,
): UserTicketMetrics {
  const assignedTickets = tickets.filter((ticket) => ticket.assignedTo === assignee)
  const countedActiveTickets = assignedTickets.filter(
    (ticket) => ticket.status !== 'Closed' && ticket.status !== 'On Hold',
  )
  const resolvedTickets = assignedTickets.filter(
    (ticket) => ticket.status === 'Closed' && ticket.resolutionTime > 0,
  )

  return {
    activeTickets: countedActiveTickets.length,
    avgResponseTime: average(
      assignedTickets
        .filter((ticket) => ticket.status !== 'On Hold' && ticket.firstResponseTime > 0)
        .map((ticket) => ticket.firstResponseTime),
    ),
    avgResolutionTime: average(
      resolvedTickets.map(getAdjustedResolutionHours),
    ),
  }
}

export function getTicketMetrics(tickets: Ticket[]): TicketMetrics {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const countedTickets = tickets.filter((ticket) => ticket.status !== 'On Hold')
  const resolvedTickets = countedTickets.filter(
    (ticket) => ticket.status === 'Closed' && ticket.resolutionTime > 0,
  )
  const activeTickets = countedTickets.filter((ticket) => ticket.status !== 'Closed')

  return {
    activeTickets: activeTickets.length,
    receivedToday: tickets.filter(
      (ticket) => new Date(ticket.createdDate).getTime() >= todayStart,
    ).length,
    avgFirstResponse: average(
      countedTickets
        .filter((ticket) => ticket.firstResponseTime > 0)
        .map((ticket) => ticket.firstResponseTime),
    ),
    avgResolutionTime: average(resolvedTickets.map(getAdjustedResolutionHours)),
    waitingForResponse: tickets.filter(
      (ticket) => ticket.status === 'Waiting for Response',
    ).length,
    closedToday: tickets.filter(
      (ticket) =>
        ticket.status === 'Closed' &&
        ticket.closedDate &&
        new Date(ticket.closedDate).getTime() >= todayStart,
    ).length,
    avgActiveAge: average(activeTickets.map((ticket) => getActiveDurationMinutes(ticket, now))),
  }
}

function average(values: number[]): number {
  if (!values.length) return 0
  return values.reduce((total, value) => total + value, 0) / values.length
}
